import { FORMAT_VERSION, formDocSchema } from "./document.js";
import { newChoiceId, newFieldId } from "./ids.js";
export const TEMPLATES = [
    {
        slug: "client-intake",
        name: "Client intake",
        audience: "Freelancers",
        summary: "Find out what someone needs, what they can spend, and when they need it — before the first call.",
        title: "Client intake",
        description: "Tell us a bit about your project. Takes about two minutes.",
        fields: [
            { type: "short_text", label: "What's your name?", required: true, placeholder: "Jane Doe" }, // prettier-ignore
            { type: "email", label: "Where can we reach you?", description: "We'll only use this to reply.", required: true }, // prettier-ignore
            { type: "multiple_choice", label: "What can we help with?", required: true, choices: ["A new project", "Something ongoing", "Just exploring"] }, // prettier-ignore
            { type: "dropdown", label: "Budget range", choices: ["Under $2k", "$2k – $10k", "$10k – $50k", "$50k+", "Not sure yet"] }, // prettier-ignore
            { type: "long_text", label: "Anything else we should know?", placeholder: "The more detail the better" }, // prettier-ignore
        ],
    },
    {
        slug: "contact",
        name: "Contact form",
        audience: "Everyone",
        summary: "The one every site needs. Name, email, message — and nothing you don't.",
        title: "Get in touch",
        description: "We usually reply within a day.",
        fields: [
            { type: "short_text", label: "Your name", required: true },
            { type: "email", label: "Your email", required: true },
            { type: "long_text", label: "How can we help?", required: true, placeholder: "Tell us what you need" }, // prettier-ignore
        ],
    },
    {
        slug: "event-rsvp",
        name: "Event RSVP",
        audience: "Everyone",
        summary: "Headcount, dietary needs and plus-ones, without a spreadsheet thread.",
        title: "Will you be joining us?",
        description: "Let us know by the end of the month so we can plan numbers.",
        fields: [
            { type: "short_text", label: "Your name", required: true },
            { type: "email", label: "Email", required: true },
            { type: "multiple_choice", label: "Can you make it?", required: true, choices: ["Yes, count me in", "No, sorry", "Not sure yet"] }, // prettier-ignore
            { type: "number", label: "How many people are you bringing?" },
            { type: "checkboxes", label: "Any dietary requirements?", choices: ["Vegetarian", "Vegan", "Gluten free", "Nut allergy", "None"] }, // prettier-ignore
        ],
    },
    {
        slug: "job-application",
        name: "Job application",
        audience: "Teams",
        summary: "Collect a CV, a link and a short answer — enough to make a first call.",
        title: "Apply to join us",
        description: "We read every application. No cover letter needed.",
        fields: [
            { type: "short_text", label: "Full name", required: true },
            { type: "email", label: "Email", required: true },
            { type: "short_text", label: "Portfolio or LinkedIn", placeholder: "https://" }, // prettier-ignore
            { type: "file_upload", label: "Your CV", description: "PDF is easiest for us.", required: true }, // prettier-ignore
            { type: "long_text", label: "Why this role?", description: "A few sentences is plenty." }, // prettier-ignore
        ],
    },
    {
        slug: "feedback",
        name: "Customer feedback",
        audience: "Teams",
        summary: "A rating and one open question — the shortest form people actually finish.",
        title: "How did we do?",
        description: "Two questions, thirty seconds.",
        fields: [
            { type: "rating", label: "How likely are you to recommend us?", max: 5 }, // prettier-ignore
            { type: "long_text", label: "What would you change?", placeholder: "Be as blunt as you like" }, // prettier-ignore
            { type: "email", label: "Email, if you'd like a reply" },
        ],
    },
    {
        slug: "parent-contact",
        name: "Parent contact form",
        audience: "Teachers",
        summary: "Emergency contacts and permissions collected once, at the start of term.",
        title: "Student contact details",
        description: "Please complete one form per child before the start of term.",
        fields: [
            { type: "short_text", label: "Student's full name", required: true },
            { type: "short_text", label: "Class or year group", required: true },
            { type: "short_text", label: "Parent or guardian name", required: true }, // prettier-ignore
            { type: "email", label: "Contact email", required: true },
            { type: "short_text", label: "Emergency phone number", required: true }, // prettier-ignore
            { type: "long_text", label: "Medical notes or allergies", description: "Leave blank if none." }, // prettier-ignore
        ],
    },
    {
        slug: "bug-report",
        name: "Bug report",
        audience: "Teams",
        summary: "What happened, what should have happened, and how to see it again.",
        title: "Report a bug",
        description: "The more specific, the faster we can fix it.",
        fields: [
            { type: "short_text", label: "What went wrong?", required: true, placeholder: "One line is fine" }, // prettier-ignore
            { type: "long_text", label: "Steps to reproduce", required: true, placeholder: "1. …\n2. …\n3. …" }, // prettier-ignore
            { type: "long_text", label: "What did you expect instead?" },
            { type: "dropdown", label: "How badly is it blocking you?", choices: ["Can't work at all", "Annoying but workable", "Cosmetic"] }, // prettier-ignore
            { type: "file_upload", label: "Screenshot or recording" },
        ],
    },
    {
        slug: "volunteer-signup",
        name: "Volunteer signup",
        audience: "Everyone",
        summary: "Who is coming, when they're free, and what they'd like to do.",
        title: "Volunteer with us",
        description: "Thanks for offering — tell us when suits you.",
        fields: [
            { type: "short_text", label: "Your name", required: true },
            { type: "email", label: "Email", required: true },
            { type: "checkboxes", label: "When are you free?", required: true, choices: ["Weekday mornings", "Weekday afternoons", "Weekday evenings", "Weekends"] }, // prettier-ignore
            { type: "checkboxes", label: "What would you like to help with?", choices: ["Setup and packing down", "Front of house", "Driving", "Anything that needs doing"] }, // prettier-ignore
            { type: "date", label: "First date you're available" },
        ],
    },
];
export const templateBySlug = (slug) => TEMPLATES.find((t) => t.slug === slug);
/**
 * Builds a real form document from a template.
 *
 * Fresh ids every time — see the note at the top. Runs the result through
 * formDocSchema so a malformed template fails here, in a test, rather than in
 * someone's builder.
 */
export const instantiateTemplate = (template) => {
    const fields = template.fields.map((field) => {
        const base = {
            id: newFieldId(),
            type: field.type,
        };
        if (field.type === "heading" || field.type === "paragraph") {
            base.text = field.text ?? field.label ?? "";
            return base;
        }
        base.label = field.label ?? "Question";
        base.required = field.required ?? false;
        if (field.description)
            base.description = field.description;
        if (field.placeholder)
            base.placeholder = field.placeholder;
        if (field.max !== undefined)
            base.max = field.max;
        if (field.choices) {
            base.choices = field.choices.map((label) => ({
                id: newChoiceId(),
                label,
            }));
        }
        return base;
    });
    return formDocSchema.parse({
        formatVersion: FORMAT_VERSION,
        title: template.title,
        description: template.description,
        fields,
        settings: {},
    });
};
//# sourceMappingURL=templates.js.map